import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Form, Icon, Input, Modal, Select, Spin, Table, message } from 'antd';
import { withRouter } from 'react-router-dom';
import { createServicePackage, deleteServicePackage, listServicePackages, updateServicePackage } from '../api/ownerReporting';
import { loadHostProperties, loadHostUnits, loadUnitsForProperty } from '../api/hostListings';
import { useOwnerReportingHost } from '../hooks/useOwnerReportingHost';
import { ALL_PROPERTIES, readPageFilters, useSyncPageFilters } from './pageFilters';
import CloneFromV1Wizard from './servicePackages/CloneFromV1Wizard';
import ServicePackageFormFields from './servicePackages/ServicePackageFormFields';
import {
  buildServicePackagePayload,
  formValuesFromPreview,
  assignedUnitsById,
  makeEmptyForm,
  normalizeId,
  syncUnitShareRates,
  unitAssignment,
  validateServicePackageForm,
} from './servicePackages/formShared';

const propertyIdOfUnit = unit => {
  const property = unit && unit.roomType && unit.roomType.property;
  if (!property) return '';
  if (typeof property === 'object') return String(property._id || '');
  return String(property);
};

const unitNamesOfPackage = (record, unitNameById) =>
  (record && record.units ? record.units : []).map(uid => {
    const id = normalizeId(uid);
    return unitNameById[id] || id || '';
  });

const propertyNameOfUnit = (unit, propertyNameById) => {
  const property = unit && unit.roomType && unit.roomType.property;
  if (property && typeof property === 'object' && property.name) return property.name;
  const pid = propertyIdOfUnit(unit);
  if (pid && propertyNameById[pid]) return propertyNameById[pid];
  return '';
};

const ServicePackagesPage = ({ location, history }) => {
  const { hostId } = useOwnerReportingHost();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [cloneVisible, setCloneVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState(makeEmptyForm);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [hostUnits, setHostUnits] = useState([]);
  const [unitNameById, setUnitNameById] = useState({});
  const [filterPropertyId, setFilterPropertyId] = useState(
    () => readPageFilters(location && location.search).propertyId || ALL_PROPERTIES
  );
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [hostUnitsLoading, setHostUnitsLoading] = useState(false);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [unitQuery, setUnitQuery] = useState('');

  const rememberUnitNames = unitList => {
    setUnitNameById(prev => {
      const next = { ...prev };
      (unitList || []).forEach(u => {
        next[String(u._id)] = u.name || String(u._id);
      });
      return next;
    });
  };

  const load = async () => {
    if (!hostId) return;
    setLoading(true);
    setPropertiesLoading(true);
    setHostUnitsLoading(true);
    try {
      const pkgs = await listServicePackages(hostId);
      setRows(pkgs || []);
    } catch (e) {
      message.error('Failed to load service packages');
      setRows([]);
    } finally {
      setLoading(false);
    }

    try {
      const [props, allUnits] = await Promise.all([loadHostProperties(hostId), loadHostUnits(hostId)]);
      setProperties(props || []);
      setHostUnits(allUnits || []);
      rememberUnitNames(allUnits || []);
      if (!(props || []).length) {
        message.warning('No properties found for this host');
      }
    } catch (e) {
      setProperties([]);
      setHostUnits([]);
      message.error('Failed to load properties');
    } finally {
      setPropertiesLoading(false);
      setHostUnitsLoading(false);
    }
  };

  useEffect(() => {
    load();
    setValues(makeEmptyForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostId]);

  useEffect(() => {
    if (!hostId || propertiesLoading) return;
    if (!filterPropertyId || filterPropertyId === ALL_PROPERTIES) return;
    if (properties.some(p => String(p._id) === String(filterPropertyId))) return;
    setFilterPropertyId(ALL_PROPERTIES);
  }, [hostId, propertiesLoading, properties, filterPropertyId]);

  useSyncPageFilters(history, location, {
    property: filterPropertyId,
  });

  useEffect(() => {
    if (!values.propertyId) {
      setUnits([]);
      return;
    }
    let cancelled = false;
    setUnitsLoading(true);

    const fromHost = (hostUnits || []).filter(u => {
      const property = u.roomType && u.roomType.property;
      const propertyId = property && property._id ? property._id : property;
      return String(propertyId) === String(values.propertyId);
    });

    if (fromHost.length) {
      setUnits(fromHost);
      rememberUnitNames(fromHost);
      setUnitsLoading(false);
      return;
    }

    loadUnitsForProperty(values.propertyId)
      .then(list => {
        if (cancelled) return;
        setUnits(list || []);
        rememberUnitNames(list || []);
      })
      .finally(() => {
        if (!cancelled) setUnitsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [values.propertyId, hostUnits]);

  const set = key => val => setValues(prev => ({ ...prev, [key]: val }));

  const openCreate = () => {
    setEditing(null);
    setValues(makeEmptyForm());
    setVisible(true);
  };

  const openEdit = record => {
    setEditing(record);
    setValues(formValuesFromPreview(record, hostUnits));
    setVisible(true);
  };

  const onPropertyChange = propertyId => {
    setValues(prev => ({ ...prev, propertyId: propertyId ? String(propertyId) : undefined }));
  };

  const assignedById = useMemo(() => assignedUnitsById(rows, editing && editing._id), [rows, editing]);

  const onUnitsChange = selected => {
    setValues(prev => {
      const currentPropertyUnitIds = new Set(units.map(u => String(u._id)));
      const kept = (prev.units || []).filter(id => !currentPropertyUnitIds.has(String(id)));
      const merged = Array.from(
        new Set(
          [...kept, ...(selected || []).map(String)].filter(id => !unitAssignment(id, assignedById))
        )
      );
      return {
        ...prev,
        units: merged,
        unitShareRates: prev.isGroupPackage ? syncUnitShareRates(merged, prev.unitShareRates) : prev.unitShareRates,
      };
    });
  };

  const onGroupPackageChange = checked => {
    setValues(prev => ({
      ...prev,
      isGroupPackage: checked,
      unitShareRates: checked ? syncUnitShareRates(prev.units || [], prev.unitShareRates) : [],
    }));
  };

  const onUnitSharePercentageChange = (unitId, ownerSharePercentage) => {
    setValues(prev => ({
      ...prev,
      unitShareRates: (prev.unitShareRates || []).map(item =>
        String(item.unitId) === String(unitId)
          ? { ...item, ownerSharePercentage: ownerSharePercentage == null ? 0 : Number(ownerSharePercentage) }
          : item
      ),
    }));
  };

  const onSubmit = async () => {
    const error = validateServicePackageForm(values, assignedById);
    if (error) {
      message.error(error);
      return;
    }
    if (!hostId) {
      message.error('Select a host first');
      return;
    }
    const payload = buildServicePackagePayload(values);
    try {
      if (editing) {
        await updateServicePackage(hostId, editing._id, payload);
      } else {
        await createServicePackage(hostId, payload);
      }
      message.success('Saved');
      setVisible(false);
      load();
    } catch (e) {
      message.error((e && e.message) || 'Save failed');
    }
  };

  const onDelete = record => {
    Modal.confirm({
      title: 'Delete service package?',
      onOk: async () => {
        await deleteServicePackage(hostId, record._id);
        message.success('Deleted');
        load();
      },
    });
  };

  const propertyNameById = useMemo(() => {
    const map = {};
    (properties || []).forEach(p => {
      map[String(p._id)] = p.name || String(p._id);
    });
    return map;
  }, [properties]);

  const propertyNamesForPackage = record => {
    const names = [];
    const seen = {};
    (record.units || []).forEach(uid => {
      const unit = (hostUnits || []).find(u => String(u._id) === normalizeId(uid));
      const name = propertyNameOfUnit(unit, propertyNameById);
      if (name && !seen[name]) {
        seen[name] = true;
        names.push(name);
      }
    });
    return names.length ? names.join(', ') : '—';
  };

  const filteredRows =
    !filterPropertyId || filterPropertyId === ALL_PROPERTIES
      ? rows
      : rows.filter(r => {
          const propertyUnitIds = new Set(
            (hostUnits || [])
              .filter(u => String(propertyIdOfUnit(u)) === String(filterPropertyId))
              .map(u => String(u._id))
          );
          if (!propertyUnitIds.size) return false;
          return (r.units || []).some(uid => propertyUnitIds.has(normalizeId(uid)));
        });

  const displayRows = useMemo(() => {
    const q = String(unitQuery || '')
      .trim()
      .toLowerCase();
    if (!q) return filteredRows;
    return (filteredRows || []).filter(r =>
      unitNamesOfPackage(r, unitNameById).some(name =>
        String(name)
          .toLowerCase()
          .includes(q)
      )
    );
  }, [filteredRows, unitQuery, unitNameById]);

  const selectedUnitsForCurrentProperty = (values.units || []).filter(id => units.some(u => String(u._id) === String(id)));

  return (
    <Card
      title="Owner Reporting — Service Packages"
      extra={
        <span>
          <Button onClick={() => setCloneVisible(true)} disabled={!hostId} style={{ marginRight: 8 }}>
            Clone from v1
          </Button>
          <Button type="primary" onClick={openCreate} disabled={!hostId}>
            New Package
          </Button>
        </span>
      }
    >
      <Form layout="vertical" style={{ marginBottom: 16 }}>
        <Form.Item label="Filter Property" style={{ marginBottom: 0 }}>
          <Select
            showSearch
            optionFilterProp="children"
            placeholder="All"
            style={{ width: 280 }}
            value={filterPropertyId || ALL_PROPERTIES}
            onChange={val => {
              setUnitQuery('');
              setFilterPropertyId(val ? String(val) : ALL_PROPERTIES);
            }}
            disabled={!hostId}
            loading={propertiesLoading}
          >
            <Select.Option value={ALL_PROPERTIES}>All</Select.Option>
            {properties.map(p => (
              <Select.Option key={String(p._id)} value={String(p._id)}>
                {p.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
      <Table
        rowKey="_id"
        loading={loading}
        dataSource={displayRows}
        columns={[
          { title: 'Name', dataIndex: 'name' },
          {
            title: 'Property Name',
            render: r => (hostUnitsLoading ? <Spin size="small" /> : propertyNamesForPackage(r)),
          },
          {
            title: 'Agreement Type',
            dataIndex: 'packageType',
            render: v => (v === 'tenancy-agreement' ? 'Fixed Rental' : 'Profit Sharing'),
          },
          {
            title: 'Package Type',
            dataIndex: 'groupingType',
            render: v => (v === 'individual' ? 'Individual' : 'Pool'),
          },
          { title: 'Share Type', dataIndex: 'shareType' },
          {
            title: 'Owner %',
            dataIndex: 'ownerSharePercentage',
            render: (value, record) => {
              if (record.isGroupPackage) {
                const rates = (record.unitShareRates || [])
                  .map(item => `${Number(item.ownerSharePercentage) || 0}%`)
                  .join(', ');
                return rates || 'Per unit';
              }
              return value == null ? '' : `${value}%`;
            },
          },
          {
            title: 'Units',
            key: 'units',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
              <div style={{ padding: 8 }}>
                <Input
                  placeholder="Search unit"
                  value={selectedKeys[0]}
                  onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                  onPressEnter={() => {
                    setUnitQuery((selectedKeys && selectedKeys[0]) || '');
                    confirm();
                  }}
                  style={{ width: 220, marginBottom: 8, display: 'block' }}
                />
                <Button
                  type="primary"
                  icon="search"
                  size="small"
                  style={{ width: 100, marginRight: 8 }}
                  onClick={() => {
                    setUnitQuery((selectedKeys && selectedKeys[0]) || '');
                    confirm();
                  }}
                >
                  Search
                </Button>
                <Button
                  size="small"
                  style={{ width: 90 }}
                  onClick={() => {
                    setUnitQuery('');
                    if (clearFilters) clearFilters();
                    confirm();
                  }}
                >
                  Reset
                </Button>
              </div>
            ),
            filterIcon: filtered => (
              <Icon type="search" style={{ color: filtered || unitQuery ? '#00b9c6' : '#aaa' }} />
            ),
            render: r => {
              if (hostUnitsLoading) return <Spin size="small" />;
              const names = unitNamesOfPackage(r, unitNameById);
              if (!names.length) return 0;
              const shown = names.slice(0, 3);
              const more = names.length > 3 ? ` +${names.length - 3}` : '';
              return `${shown.join(', ')}${more}`;
            },
          },
          {
            title: 'Actions',
            render: r => (
              <span>
                <Button type="link" onClick={() => openEdit(r)}>
                  Edit
                </Button>
                <Button type="link" style={{ color: '#f5222d' }} onClick={() => onDelete(r)}>
                  Delete
                </Button>
              </span>
            ),
          },
        ]}
      />
      <Modal
        visible={visible}
        title={editing ? 'Edit Package' : 'New Package'}
        onOk={onSubmit}
        onCancel={() => setVisible(false)}
        destroyOnClose
        width={720}
        style={{ top: 24 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}
      >
        <ServicePackageFormFields
          values={values}
          set={set}
          properties={properties}
          propertiesLoading={propertiesLoading}
          units={units}
          unitsLoading={unitsLoading}
          selectedUnitsForCurrentProperty={selectedUnitsForCurrentProperty}
          unitNameById={unitNameById}
          onPropertyChange={onPropertyChange}
          onUnitsChange={onUnitsChange}
          onGroupPackageChange={onGroupPackageChange}
          onUnitSharePercentageChange={onUnitSharePercentageChange}
          assignedById={assignedById}
        />
      </Modal>
      <CloneFromV1Wizard
        visible={cloneVisible}
        hostId={hostId}
        existingPackages={rows}
        properties={properties}
        propertiesLoading={propertiesLoading}
        hostUnits={hostUnits}
        unitNameById={unitNameById}
        rememberUnitNames={rememberUnitNames}
        onClose={() => setCloneVisible(false)}
        onCreated={() => {
          setCloneVisible(false);
          load();
        }}
      />
    </Card>
  );
};

export default withRouter(ServicePackagesPage);
